import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { getMediaType, useUpload } from '@/contexts';
import { MediaType } from '@/types';
import { unreachable } from '@/utils';
import classnames from 'classnames';
import { useState } from 'react';
import { Icon, LoadingIcon } from '../Icon';
import { useLocales } from '../useLocales';
import styles from './index.module.less';
const getMaxSize = (mediaType) => {
    switch (mediaType) {
        case MediaType.Video:
        case MediaType.Audio:
            return 30 * 1024 * 1024;
        case MediaType.Image:
        case MediaType.Unknown:
            return 2 * 1024 * 1024;
        case MediaType.Csv:
            return 10 * 1024 * 1024;
        default:
            unreachable(mediaType);
    }
};
const FILE_EXT_RE = /.csv/;
export const UploadButton = ({ className, fileName, onUploaded, onStartUpload, onError, disabled = false, ...rest }) => {
    const t = useLocales();
    const { upload, uploading } = useUpload();
    const [invalidMessage, setInvalidMessage] = useState('');
    const [dragingIn, setDragingIn] = useState(false);
    const handleUpload = async (file) => {
        setInvalidMessage('');
        try {
            onStartUpload?.();
            await upload(file, (f) => {
                const mediaType = getMediaType(f.name);
                const maxSize = getMaxSize(mediaType);
                if (f.size > maxSize) {
                    throw new Error(t('File size up to {size}MB', { size: maxSize / (1024 * 1024) }));
                }
                else if (!f.name.toLowerCase().match(FILE_EXT_RE)) {
                    throw new Error(t('You should upload a file, only accept csv'));
                }
                else {
                    const fileReader = new FileReader();
                    fileReader.readAsText(f);
                    fileReader.onload = function () {
                        const addressList = this.result?.toString()?.split(/\s+/);
                        onUploaded?.({
                            fileAddressList: addressList,
                            whitelistFileName: f.name,
                        });
                    };
                    fileReader.onerror = function () {
                        console.log('Failed to read file!' + fileReader.error);
                        throw new Error(`Failed to read file: ${fileReader.error}`);
                    };
                }
            });
        }
        catch (err) {
            const message = err.message;
            setInvalidMessage(message);
            onError?.(err);
        }
    };
    return (_jsxs("div", { className: classnames(className, styles.UploadButton, {
            [styles.dragingIn]: dragingIn,
            [styles.error]: !!invalidMessage,
        }), onDragOver: (evt) => {
            evt.nativeEvent.preventDefault();
            setDragingIn(true);
        }, onDragLeave: (evt) => {
            evt.nativeEvent.preventDefault();
            setDragingIn(false);
        }, onDrop: async (evt) => {
            evt.nativeEvent.preventDefault();
            setDragingIn(false);
            const firstFile = evt.dataTransfer.files[0];
            if (!firstFile)
                return;
            if (firstFile.name.toLowerCase().match(FILE_EXT_RE)) {
                handleUpload(firstFile);
            }
            else {
                setInvalidMessage(t('You should upload a file, only csv is supposed'));
                onError?.(new Error(t('You should upload a file, only csv is supposed')));
            }
        }, onClick: () => (disabled ? undefined : handleUpload()), onKeyDown: (evt) => {
            if (evt.code === 'Enter') {
                handleUpload();
            }
        }, ...rest, children: [fileName ? (_jsxs("div", { className: classnames(styles.uploadBox, styles.uploadedBox), children: [fileName, _jsx(Icon, { onClick: () => disabled
                            ? undefined
                            : onUploaded && onUploaded({ fileAddressList: undefined, whitelistFileName: '' }), size: 24, type: "close" })] })) : (_jsxs("div", { className: styles.uploadBox, children: [uploading ? _jsx(LoadingIcon, { size: 24 }) : _jsx(Icon, { size: 24, type: "uploadButton" }), _jsx("div", { className: styles.description, children: t('Drag or Choose file') })] })), _jsx("div", { className: styles.rightEnd, children: "(.csv)" })] }));
};
